/**
 * Board meetings — from scheduling one to issuing its minutes.
 *
 * Two screens in one page, the same shape as the statutory registers: a list of
 * the company's meetings, and one meeting open. The open meeting is laid out as
 * the pipeline it is, because the work happens over days rather than in one
 * sitting and a user returning to it needs to see immediately where it got to:
 *
 *   scheduled ─▶ processing ─▶ transcript ready ─▶ draft ready ─▶ approved
 *
 * A recording arrives by upload or by pressing Record here; both produce the same
 * transcript, so only RecordingPanel knows there is more than one way in. Once
 * there is a transcript the speakers are named, the minutes are drafted by the
 * engine that already knows SS-1 and SS-2, and approval locks the record.
 *
 * While a meeting is transcribing the page polls, because transcription takes
 * minutes and there is no push channel for it. Polling stops the moment the
 * status settles, and when the tab is hidden.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TbCalendarEvent, TbPlus, TbArrowLeft, TbTrash, TbEdit, TbFileText, TbCheck,
  TbLoader2, TbAlertTriangle, TbRefresh, TbUsers, TbLock, TbExternalLink,
} from 'react-icons/tb';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import Modal from '../components/common/Modal';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { SkeletonBar } from '../components/common/Skeleton';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import { meetingsAPI } from '../services/api';
import { ROUTES } from '../constants/constants';
import MeetingForm from '../components/meetings/MeetingForm';
import RecordingPanel from '../components/meetings/RecordingPanel';
import TranscriptPanel from '../components/meetings/TranscriptPanel';
import DraftReviewPanel from '../components/meetings/DraftReviewPanel';
import MeetingOverview from '../components/meetings/MeetingOverview';
import PipelineStrip from '../components/meetings/PipelineStrip';

/** How the pipeline states read to a person, and how they colour. */
const STATUS = {
  scheduled: { label: 'Scheduled', tone: '#7a7060' },
  processing: { label: 'Transcribing', tone: '#2c5560' },
  transcript_ready: { label: 'Transcript ready', tone: '#145f5f' },
  draft_ready: { label: 'Draft ready', tone: '#8a6d2f' },
  approved: { label: 'Approved', tone: '#2c6045' },
  failed: { label: 'Failed', tone: '#a83c2c' },
};

const TYPE_LABEL = {
  board: 'Board', committee: 'Committee', agm: 'AGM', egm: 'EGM',
};

const POLL_MS = 8000;

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/* ── The list ────────────────────────────────────────────────────────────── */

function MeetingList({ meetings, onOpen, onNew }) {
  if (!meetings.length) {
    return (
      <EmptyState
        icon={TbCalendarEvent}
        title="No meetings yet"
        subtitle="Create a meeting, hold it, then upload the recording — the minutes are drafted from what was actually said."
        action={<Button variant="primary" icon={TbPlus} onClick={onNew}>New meeting</Button>}
      />
    );
  }

  return (
    <div className="space-y-2">
      {meetings.map((m) => {
        const status = STATUS[m.status] || STATUS.scheduled;
        return (
          <button
            key={m._id}
            type="button"
            onClick={() => onOpen(m._id)}
            className="flex w-full flex-wrap items-center gap-3 rounded-xl border-[1.5px] border-[#ddd6c6] bg-white px-4 py-3 text-left hover:border-[#145f5f]"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-bold text-[#2c2a24]">{m.title}</div>
              <div className="mt-0.5 text-[11px] text-[#8a8172]">
                {TYPE_LABEL[m.meetingType] || m.meetingType}
                {m.committeeName ? ` · ${m.committeeName}` : ''}
                {' · '}{formatDate(m.heldOn)}
                {m.venue ? ` · ${m.venue}` : ''}
              </div>
            </div>
            <Chip tone={status.tone}>{status.label}</Chip>
          </button>
        );
      })}
    </div>
  );
}

/* ── One meeting ─────────────────────────────────────────────────────────── */

function MeetingDetail({
  companyId, meeting, transcript, transcription, onBack, onEdit, onDelete, onReload, showToast, onOpenDraft,
}) {
  const [busy, setBusy] = useState('');
  const status = STATUS[meeting.status] || STATUS.scheduled;
  const locked = meeting.status === 'approved';

  const run = async (key, fn, successMessage) => {
    setBusy(key);
    try {
      await fn();
      if (successMessage) showToast(successMessage, 'success');
      await onReload();
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    } finally {
      setBusy('');
    }
  };

  const uploadRecording = (file, source, consentConfirmed = false) =>
    run('upload', () => meetingsAPI.uploadRecording(companyId, meeting._id, file, source, consentConfirmed),
      'Recording received — transcription has started');

  const saveSpeakers = (map) =>
    run('speakers', () => meetingsAPI.setSpeakers(companyId, meeting._id, map), 'Speakers saved');

  // Two buttons call this, so they need distinct keys — sharing one made both
  // spinners run whichever was pressed.
  const generate = (withoutTranscript = false) =>
    run(withoutTranscript ? 'generate-plain' : 'generate',
      () => meetingsAPI.draftMinutes(companyId, meeting._id, { withoutTranscript }),
      'Minutes drafted');

  const approve = () =>
    run('approve', () => meetingsAPI.approve(companyId, meeting._id), 'Minutes approved and locked');

  const quorum = meeting.quorum;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ink" size="sm" icon={TbArrowLeft} onClick={onBack}>All meetings</Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[18px] font-bold text-[#2c2a24]">{meeting.title}</h2>
            <Chip tone={status.tone}>{status.label}</Chip>
            {locked && <Chip tone='#7a7060'><TbLock size={11} className="mr-1 inline" />Locked</Chip>}
          </div>
          <p className="mt-0.5 text-[12px] text-[#8a8172]">
            {TYPE_LABEL[meeting.meetingType]} · {formatDate(meeting.heldOn)}
            {meeting.startTime ? ` · ${meeting.startTime}` : ''}
            {meeting.endTime ? ` to ${meeting.endTime}` : ''}
          </p>
        </div>
        {!locked && (
          <div className="flex gap-2">
            <Button variant="ink" size="sm" icon={TbEdit} onClick={onEdit}>Edit</Button>
            <Button variant="danger" size="sm" icon={TbTrash} onClick={onDelete}>Delete</Button>
          </div>
        )}
      </div>

      {/* Where this is in the pipeline. The work spans days, so the first thing
          a returning user needs is how far it has got and what is next. */}
      <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-[#faf8f3] px-4 py-3">
        <PipelineStrip status={meeting.status} />
      </div>

      {/* Quorum, stated rather than left to be worked out. Counted against the
          whole board under S.174(1), not against who was invited. */}
      {quorum?.applicable && (
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] ${
          quorum.met
            ? 'border-[#b8d4c4] bg-[#f0f7f3] text-[#2c6045]'
            : 'border-[#e3c9a0] bg-[#fdf6e9] text-[#7a5a2a]'
        }`}>
          <TbUsers size={15} className="shrink-0" />
          <span>
            {quorum.present} of {quorum.totalStrength} directors present · {quorum.required} required
            {quorum.met ? ' · quorum met' : ' · quorum NOT met'}
          </span>
        </div>
      )}

      <MeetingOverview meeting={meeting} />

      {meeting.status === 'failed' && (
        <div className="rounded-lg border border-[#e0b4b4] bg-[#fdf1f1] px-4 py-3">
          <div className="mb-1 flex items-center gap-2 text-[13px] font-bold text-[#8a3a3a]">
            <TbAlertTriangle size={15} /> Transcription failed
          </div>
          <p className="mb-3 text-[12px] text-[#8a3a3a]">{meeting.failureReason}</p>
          {/* The audio is deliberately kept when a run fails, which is what makes
              this button meaningful rather than a re-upload prompt. */}
          <Button
            variant="danger" size="sm" icon={TbRefresh} loading={busy === 'retry'}
            onClick={() => run('retry', () => meetingsAPI.retryTranscription(companyId, meeting._id), 'Transcription restarted')}
          >
            Try again
          </Button>
        </div>
      )}

      {meeting.status === 'processing' && (
        <div className="flex items-center gap-2 rounded-lg border border-[#b9cfd6] bg-[#f0f6f8] px-4 py-3 text-[13px] text-[#2c5560]">
          <TbLoader2 size={16} className="animate-spin" />
          Transcribing the recording. This takes a few minutes for a long meeting — you can leave this page.
        </div>
      )}

      {meeting.audioDiscarded && (
        <p className="text-[11px] text-[#8a8172]">
          The audio was deleted once the transcript was made. There is no recording held.
        </p>
      )}

      {!locked && ['scheduled', 'failed'].includes(meeting.status) && (
        <RecordingPanel
          meeting={meeting}
          transcription={transcription}
          busy={busy === 'upload'}
          onSubmit={uploadRecording}
        />
      )}

      {transcript?.segments?.length > 0 && (
        <TranscriptPanel
          transcript={transcript}
          saving={busy === 'speakers'}
          onSaveSpeakers={saveSpeakers}
        />
      )}

      {/* What the draft claims against what was said. Read this before the
          draft — it is the list of things to verify by ear. */}
      {meeting.draft && meeting.draftReview && (
        <DraftReviewPanel review={meeting.draftReview} />
      )}

      {/* ── Drafting ─────────────────────────────────────────────────────── */}
      {!locked && (
        <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-5">
          <h3 className="mb-1 text-[15px] font-bold text-[#2c2a24]">Minutes</h3>
          <p className="mb-4 text-[12px] text-[#6b6252]">
            Drafted under SS-1 / SS-2 from the agenda and, where there is one, the transcript.
          </p>

          {/* Every action here is disabled while any one of them runs. Drafting
              takes seconds and approving mid-draft would race the two writes
              against each other on the same meeting. */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary" icon={TbFileText} loading={busy === 'generate'}
              disabled={!!busy || !meeting.agendaItems?.length}
              onClick={() => generate(false)}
            >
              {meeting.draft ? 'Redraft minutes' : 'Draft minutes'}
            </Button>

            {/* Drafting from the agenda alone is what the Minutes Studio has
                always done, so it stays available — just never by accident. */}
            {!transcript?.segments?.length && (
              <Button
                variant="ink" loading={busy === 'generate-plain'}
                disabled={!!busy || !meeting.agendaItems?.length}
                onClick={() => generate(true)}
              >
                Draft without a recording
              </Button>
            )}

            {/* The draft is a real saved document — this is how it gets edited,
                exported and page-numbered. Without it the minutes are generated
                and then unreachable from where they were made. */}
            {meeting.draft && (
              <Button
                variant="ink" icon={TbExternalLink} disabled={!!busy}
                onClick={() => onOpenDraft(meeting.draft)}
              >
                Open in Minutes Studio
              </Button>
            )}

            {meeting.draft && (
              <Button
                variant="success" icon={TbCheck} loading={busy === 'approve'}
                disabled={!!busy}
                onClick={approve}
                title={meeting.endTime ? undefined : 'Record the time the meeting concluded first'}
              >
                Approve and lock
              </Button>
            )}
          </div>

          {/* Asked for BEFORE drafting, not at approval. The conclusion time is
              printed in the minutes, so recording it later invalidates the draft
              and the whole thing has to be drafted again — approving first and
              being told afterwards walks the user straight into that. */}
          {!meeting.endTime && (
            <p className="mt-3 text-[11px] text-[#8a6d2f]">
              {transcript?.segments?.length
                ? 'SS-1 requires the time the meeting concluded on the minutes. If the recording states it, drafting takes it from there; otherwise add it before drafting, since adding it afterwards discards the draft.'
                : 'Add the time the meeting concluded before drafting — SS-1 requires it on the minutes, and adding it afterwards discards the draft.'}
            </p>
          )}

          {meeting.draft && (
            <p className="mt-3 text-[11px] text-[#8a8172]">
              Editing this meeting discards the draft, since the minutes would no longer match it.
            </p>
          )}

          {!meeting.agendaItems?.length && (
            <p className="mt-3 text-[11px] text-[#8a6d2f]">Add at least one agenda item first.</p>
          )}
        </div>
      )}

      {locked && (
        <div className="rounded-xl border-[1.5px] border-[#b8d4c4] bg-[#f0f7f3] p-5">
          <div className="mb-1 flex items-center gap-2 text-[14px] font-bold text-[#2c6045]">
            <TbCheck size={16} /> Minutes approved
          </div>
          <p className="text-[12px] text-[#3d6b52]">
            Approved on {formatDate(meeting.approvedAt)}. The meeting and its minutes are locked.
          </p>
          {meeting.draft && (
            <div className="mt-3">
              <Button variant="ink" size="sm" icon={TbExternalLink} onClick={() => onOpenDraft(meeting.draft)}>
                Open the minutes
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── The page ────────────────────────────────────────────────────────────── */

export default function MeetingsPage() {
  const { activeCompany } = useApp();
  const { showToast } = useUI();
  const navigate = useNavigate();

  const companyId = activeCompany?._id || activeCompany?.id || null;

  const [meetings, setMeetings] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [directors, setDirectors] = useState([]);
  const [transcription, setTranscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await meetingsAPI.list(companyId);
      setMeetings(res.data?.meetings || []);
    } catch (err) {
      showToast(err.message || 'Could not load meetings', 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  const loadMeeting = useCallback(async (id) => {
    if (!companyId || !id) return;
    try {
      const [detail, script] = await Promise.all([
        meetingsAPI.get(companyId, id),
        meetingsAPI.getTranscript(companyId, id),
      ]);
      setMeeting(detail.data?.meeting || null);
      setTranscript(script.data || null);
    } catch (err) {
      showToast(err.message || 'Could not load the meeting', 'error');
    }
  }, [companyId, showToast]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!companyId) return;
    meetingsAPI.eligibleDirectors(companyId)
      .then((res) => setDirectors(res.data?.directors || []))
      .catch(() => setDirectors([]));
    meetingsAPI.transcriptionStatus(companyId)
      .then((res) => setTranscription(res.data || null))
      .catch(() => setTranscription(null));
  }, [companyId]);

  useEffect(() => {
    if (openId) loadMeeting(openId);
    else { setMeeting(null); setTranscript(null); }
  }, [openId, loadMeeting]);

  /**
   * Poll while a transcript is being made. Transcription runs in the background
   * with no push channel, so the status is the only signal — and polling a
   * settled meeting, or a hidden tab, is pure waste.
   */
  useEffect(() => {
    clearInterval(pollRef.current);
    if (meeting?.status !== 'processing') return undefined;

    pollRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') loadMeeting(meeting._id);
    }, POLL_MS);

    return () => clearInterval(pollRef.current);
  }, [meeting?.status, meeting?._id, loadMeeting]);

  const submitForm = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await meetingsAPI.update(companyId, editing._id, values);
        showToast('Meeting updated', 'success');
        await loadMeeting(editing._id);
      } else {
        const res = await meetingsAPI.create(companyId, values);
        showToast('Meeting created', 'success');
        setOpenId(res.data?.meeting?._id || null);
      }
      setFormOpen(false);
      setEditing(null);
      await loadList();
    } catch (err) {
      showToast(err.message || 'Could not save the meeting', 'error');
    } finally {
      setSaving(false);
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteMeeting = async () => {
    setDeleting(true);
    try {
      await meetingsAPI.remove(companyId, meeting._id);
      showToast('Meeting deleted', 'success');
      setOpenId(null);
      await loadList();
    } catch (err) {
      showToast(err.message || 'Could not delete the meeting', 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (!companyId) {
    return (
      <AppLayout pageTitle="Board Meetings">
        <EmptyState
          icon={TbCalendarEvent}
          title="Select a company"
          subtitle="Meetings are kept per company. Choose one to see its meetings."
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Board Meetings">
      <PageHeader
        title="Board Meetings"
        subtitle={`Minutes drafted from what was actually said · ${activeCompany?.name || ''}`}
        actions={!openId && (
          <Button variant="primary" icon={TbPlus} onClick={() => { setEditing(null); setFormOpen(true); }}>
            New meeting
          </Button>
        )}
      />

      {openId && meeting ? (
        <MeetingDetail
          companyId={companyId}
          meeting={meeting}
          transcript={transcript}
          transcription={transcription}
          showToast={showToast}
          onBack={() => setOpenId(null)}
          onEdit={() => { setEditing(meeting); setFormOpen(true); }}
          onDelete={() => setConfirmDelete(true)}
          onReload={async () => { await loadMeeting(meeting._id); await loadList(); }}
          onOpenDraft={(draftId) => navigate(ROUTES.MINUTES, { state: { draftId } })}
        />
      ) : loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white px-4 py-3">
              <SkeletonBar w="42%" h={13} />
              <div className="mt-2"><SkeletonBar w="28%" h={10} /></div>
            </div>
          ))}
        </div>
      ) : (
        <MeetingList
          meetings={meetings}
          onOpen={setOpenId}
          onNew={() => { setEditing(null); setFormOpen(true); }}
        />
      )}

      {/* 'xl', not 'lg': Modal only knows default|wide|xl, and an unknown size
          falls back to the 560px default — too narrow for the two-column grid and
          the attendance list, which is what made this form scroll in a cramped box. */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={deleteMeeting}
        variant="danger"
        loading={deleting}
        title="Delete this meeting?"
        message={meeting ? `"${meeting.title}" will be removed.` : ''}
        note="Its agenda, attendance, recording and transcript go with it. This cannot be undone."
        confirmLabel="Delete meeting"
      />

      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        title={editing ? 'Edit meeting' : 'New meeting'}
        size="xl"
        bodyClassName="px-6 py-5"
        footer={(
          <>
            <Button type="button" variant="ink" onClick={() => { setFormOpen(false); setEditing(null); }}>
              Cancel
            </Button>
            {/* Submits the form by id — the actions live in the modal's footer,
                outside the scrolling body, so they sit below the fields instead
                of floating over them. */}
            <Button type="submit" form="meeting-form" variant="primary" loading={saving}>
              {editing ? 'Save changes' : 'Create meeting'}
            </Button>
          </>
        )}
      >
        <MeetingForm meeting={editing} directors={directors} onSubmit={submitForm} />
      </Modal>
    </AppLayout>
  );
}
